import { useCallback, useEffect, useRef, useState } from "react";
import { OPENAI_REALTIME_SESSION_ENDPOINT } from "duck/constants";
import { useTranslation } from "react-i18next";

import { getAuthHeaders } from "shared/utils/auth-utils";

import { toast } from "features/ui/Toast";

const OPENAI_REALTIME_CALLS_URL = "https://api.openai.com/v1/realtime/calls";

const isMediaDevicesSupported =
  typeof navigator !== "undefined" &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof RTCPeerConnection !== "undefined" &&
  typeof AudioContext !== "undefined";

interface ClientSecretResponse {
  value: string;
  expires_at: number;
}

const fetchClientSecret = async (): Promise<ClientSecretResponse> => {
  const response = await fetch(OPENAI_REALTIME_SESSION_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to create realtime session");
  }

  const data = await response.json();

  return data as ClientSecretResponse;
};

// --- Module-level token cache with background refresh ---

let cachedToken: ClientSecretResponse | null = null;
let tokenFetchPromise: Promise<ClientSecretResponse> | null = null;
let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
let hasConnectedBefore = false;

const TOKEN_REFRESH_BUFFER_MS = 60_000;

const scheduleTokenRefresh = () => {
  if (refreshTimeoutId !== null) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }

  if (!cachedToken) return;

  const expiresInMs = cachedToken.expires_at * 1000 - Date.now();
  const refreshInMs = Math.max(expiresInMs - TOKEN_REFRESH_BUFFER_MS, 0);

  refreshTimeoutId = setTimeout(async () => {
    refreshTimeoutId = null;
    try {
      tokenFetchPromise = fetchClientSecret();
      cachedToken = await tokenFetchPromise;
      tokenFetchPromise = null;
      scheduleTokenRefresh();
    } catch (error) {
      console.error("[SpeechToText] Background token refresh failed:", error);
      tokenFetchPromise = null;
      cachedToken = null;
    }
  }, refreshInMs);
};

const getOrFetchToken = async (): Promise<ClientSecretResponse> => {
  if (
    cachedToken &&
    cachedToken.expires_at * 1000 > Date.now() + TOKEN_REFRESH_BUFFER_MS
  ) {
    return cachedToken;
  }

  if (tokenFetchPromise) {
    return tokenFetchPromise;
  }

  tokenFetchPromise = fetchClientSecret();
  try {
    cachedToken = await tokenFetchPromise;
    scheduleTokenRefresh();

    return cachedToken;
  } finally {
    tokenFetchPromise = null;
  }
};

interface ActiveResources {
  pc: RTCPeerConnection;
  dc: RTCDataChannel;
  stream: MediaStream;
  audioContext: AudioContext;
  analyser: AnalyserNode;
}

const transcriptionSessionProps = {
  type: "session.update",
  session: {
    type: "transcription",
    audio: {
      input: {
        transcription: {
          model: "gpt-4o-mini-transcribe",
        },
        turn_detection: {
          type: "server_vad",
          silence_duration_ms: 800,
        },
        noise_reduction: {
          type: "near_field",
        },
      },
    },
  },
};

const useRecording = (
  onTranscriptReady: (text: string) => void,
  onPartialUpdate?: (text: string) => void,
  keepAlive?: boolean
) => {
  const { t } = useTranslation("duck");
  const onTranscriptReadyRef = useRef(onTranscriptReady);
  onTranscriptReadyRef.current = onTranscriptReady;
  const onPartialUpdateRef = useRef(onPartialUpdate);
  onPartialUpdateRef.current = onPartialUpdate;
  const keepAliveRef = useRef(keepAlive);
  keepAliveRef.current = keepAlive;

  const [isActive, setIsActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [finalText, setFinalText] = useState("");
  const [partialText, setPartialText] = useState("");
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);

  const resourcesRef = useRef<ActiveResources | null>(null);
  const speechStoppedRef = useRef(false);
  const transcriptRef = useRef("");
  const dispatchedRef = useRef(false);
  const cancelledRef = useRef(false);
  const startingRef = useRef(false);
  const accumulatedTextRef = useRef("");
  const baseTextRef = useRef("");
  // Tracks whether the component is still mounted so async continuations
  // (fetch/getUserMedia/SDP catch blocks and success path) can skip setState
  // after unmount.
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const cleanup = useCallback(() => {
    const res = resourcesRef.current;
    if (res) {
      res.dc.close();
      res.pc.close();
      res.stream.getTracks().forEach((t) => t.stop());
      if (res.audioContext.state !== "closed") {
        res.audioContext.close();
      }

      resourcesRef.current = null;
    }

    speechStoppedRef.current = false;
    transcriptRef.current = "";
    dispatchedRef.current = false;
    startingRef.current = false;
    accumulatedTextRef.current = "";
    baseTextRef.current = "";

    // Skip React state resets when called from async paths after unmount.
    // Synchronous unmount cleanup still runs these because mountedRef is only
    // flipped by the dedicated effect above.
    if (!mountedRef.current) return;

    setAnalyserNode(null);
    setIsActive(false);
    setIsConnecting(false);
    setFinalText("");
    setPartialText("");
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const startRecording = useCallback(async () => {
    if (resourcesRef.current || startingRef.current) return;

    startingRef.current = true;
    cancelledRef.current = false;

    if (!hasConnectedBefore) {
      setIsConnecting(true);
    }

    let clientSecret: ClientSecretResponse;
    try {
      clientSecret = await getOrFetchToken();
    } catch (error) {
      console.error("[SpeechToText] Failed to fetch client secret:", error);
      startingRef.current = false;
      if (!mountedRef.current) return;

      toast.error(t("stt_api_key_error"));
      setIsConnecting(false);

      return;
    }

    if (cancelledRef.current) {
      startingRef.current = false;

      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      console.error("[SpeechToText] Failed to get microphone access:", error);
      startingRef.current = false;
      if (!mountedRef.current) return;

      toast.error(t("mic_permission_error"));
      setIsConnecting(false);

      return;
    }

    if (cancelledRef.current) {
      stream.getTracks().forEach((t) => t.stop());
      startingRef.current = false;

      return;
    }

    // Set up AnalyserNode for waveform visualization
    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    source.connect(analyser);

    // Create WebRTC peer connection
    const pc = new RTCPeerConnection();

    // Add mic track to peer connection
    pc.addTrack(stream.getTracks()[0]);

    // Create data channel for events
    const dc = pc.createDataChannel("oai-events");

    resourcesRef.current = {
      pc,
      dc,
      stream,
      audioContext,
      analyser,
    };

    dc.onopen = () => {
      // Configure transcription session once the data channel is ready
      dc.send(JSON.stringify(transcriptionSessionProps));
    };

    dc.onmessage = (event) => {
      const msg = JSON.parse(event.data);

      switch (msg.type) {
        case "conversation.item.input_audio_transcription.delta": {
          const delta = msg.delta ?? "";
          accumulatedTextRef.current += delta;
          const displayText = baseTextRef.current
            ? baseTextRef.current + " " + accumulatedTextRef.current
            : accumulatedTextRef.current;
          setPartialText(displayText);
          onPartialUpdateRef.current?.(displayText);
          break;
        }

        case "conversation.item.input_audio_transcription.completed": {
          const transcript = (msg.transcript ?? "").trim();
          transcriptRef.current = transcript;
          if (transcript) {
            setFinalText(transcript);
            setPartialText("");
          }

          if (speechStoppedRef.current && !dispatchedRef.current) {
            dispatchedRef.current = true;
            if (keepAliveRef.current) {
              // Keep connection alive — accumulate completed text and reset for next utterance
              if (transcript) {
                baseTextRef.current = baseTextRef.current
                  ? baseTextRef.current + " " + transcript
                  : transcript;
                accumulatedTextRef.current = "";
                onPartialUpdateRef.current?.(baseTextRef.current);
              }

              speechStoppedRef.current = false;
              transcriptRef.current = "";
              dispatchedRef.current = false;
            } else {
              if (transcript) {
                onTranscriptReadyRef.current(transcript);
              }

              cleanup();
            }
          }

          break;
        }

        case "input_audio_buffer.speech_stopped":
          speechStoppedRef.current = true;
          if (transcriptRef.current && !dispatchedRef.current) {
            dispatchedRef.current = true;
            if (keepAliveRef.current) {
              const text = transcriptRef.current;
              baseTextRef.current = baseTextRef.current
                ? baseTextRef.current + " " + text
                : text;
              accumulatedTextRef.current = "";
              onPartialUpdateRef.current?.(baseTextRef.current);
              speechStoppedRef.current = false;
              transcriptRef.current = "";
              dispatchedRef.current = false;
            } else {
              const text = transcriptRef.current;
              onTranscriptReadyRef.current(text);
              cleanup();
            }
          }

          break;

        case "error":
          console.error("[SpeechToText] OpenAI error:", msg);
          toast.error(t("stt_connection_error"));
          cleanup();
          break;
      }
    };

    dc.onerror = () => {
      toast.error(t("stt_connection_error"));
      cleanup();
    };

    try {
      // Create SDP offer and connect via OpenAI Realtime calls endpoint
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpResponse = await fetch(OPENAI_REALTIME_CALLS_URL, {
        method: "POST",
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${clientSecret.value}`,
          "Content-Type": "application/sdp",
        },
      });

      if (!sdpResponse.ok) {
        const errorText = await sdpResponse.text();
        console.error("[SpeechToText] SDP negotiation failed:", errorText);
        throw new Error("SDP negotiation failed");
      }

      const answer: RTCSessionDescriptionInit = {
        type: "answer",
        sdp: await sdpResponse.text(),
      };
      await pc.setRemoteDescription(answer);
    } catch {
      startingRef.current = false;
      if (!mountedRef.current) {
        // Unmount cleanup already closed these resources via resourcesRef.
        return;
      }

      toast.error(t("stt_connection_error"));
      stream.getTracks().forEach((t) => t.stop());
      audioContext.close();
      pc.close();
      resourcesRef.current = null;
      setIsConnecting(false);

      return;
    }

    if (!mountedRef.current) {
      // Unmount cleanup already released the resources assigned to resourcesRef.
      return;
    }

    if (cancelledRef.current) {
      cleanup();

      return;
    }

    hasConnectedBefore = true;
    setIsConnecting(false);
    setIsActive(true);
    setAnalyserNode(analyser);
  }, [cleanup, t]);

  const cancelRecording = useCallback(() => {
    cancelledRef.current = true;
    cleanup();
  }, [cleanup]);

  return {
    startRecording,
    cancelRecording,
    isRecording: isActive,
    isConnecting,
    finalText,
    partialText,
    isSupported: isMediaDevicesSupported,
    analyserNode,
  };
};

// --- High-level mode hook (conversation mode + press-to-submit) ---

export interface SpeechToTextOverlayProps {
  isPressToSubmit: boolean;
  isConnecting: boolean;
  isRecording: boolean;
  isWaiting: boolean;
  analyserNode: AnalyserNode | null;
  hasText: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

interface UseSpeechToTextOptions {
  isPressToSubmit: boolean;
  speechToTextEnabled: boolean;
  isInputBlocked: boolean;
  sendMessage: (text: string) => void;
  setInputValue: (value: string) => void;
  inputValue: string;
}

export const useSpeechToText = ({
  isPressToSubmit,
  speechToTextEnabled,
  isInputBlocked,
  sendMessage,
  setInputValue,
  inputValue,
}: UseSpeechToTextOptions) => {
  const { t } = useTranslation("duck");
  const [conversationMode, setConversationMode] = useState(false);
  const [pressToSubmitActive, setPressToSubmitActive] = useState(false);

  // Use refs to avoid recreating callbacks when props change,
  // which would propagate into useRecording and could interrupt active recordings.
  const isPressToSubmitRef = useRef(isPressToSubmit);
  isPressToSubmitRef.current = isPressToSubmit;
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;
  const setInputValueRef = useRef(setInputValue);
  setInputValueRef.current = setInputValue;
  const inputValueRef = useRef(inputValue);
  inputValueRef.current = inputValue;

  const handleTranscriptReady = useCallback((text: string) => {
    if (isPressToSubmitRef.current) {
      // Set final text into the chat input and deactivate the overlay
      setInputValueRef.current(text);
      setPressToSubmitActive(false);
    } else {
      sendMessageRef.current(text);
    }
  }, []);

  // Stream partial transcription text into the chat input for press-to-submit mode
  const handlePartialUpdate = useCallback((text: string) => {
    if (isPressToSubmitRef.current) {
      setInputValueRef.current(text);
    }
  }, []);

  const {
    startRecording,
    cancelRecording,
    isRecording,
    isConnecting,
    isSupported: isSttSupported,
    analyserNode,
  } = useRecording(handleTranscriptReady, handlePartialUpdate, isPressToSubmit);

  const sttAvailable = speechToTextEnabled && isSttSupported;

  // --- Conversation mode logic (auto-submit) ---

  // Auto-start recording when conversation mode is toggled on
  const prevConversationMode = useRef(false);
  useEffect(() => {
    if (
      !isPressToSubmit &&
      conversationMode &&
      !prevConversationMode.current &&
      !isRecording
    ) {
      startRecording();
    }

    prevConversationMode.current = conversationMode;
  }, [isPressToSubmit, conversationMode, isRecording, startRecording]);

  // Auto-restart recording in conversation mode after streaming ends
  const prevIsInputBlocked = useRef(false);
  useEffect(() => {
    if (
      !isPressToSubmit &&
      conversationMode &&
      prevIsInputBlocked.current &&
      !isInputBlocked &&
      !isRecording
    ) {
      startRecording();
    }

    prevIsInputBlocked.current = isInputBlocked;
  }, [
    isPressToSubmit,
    conversationMode,
    isInputBlocked,
    isRecording,
    startRecording,
  ]);

  // Turn off conversation mode and cancel active recording if STT becomes unavailable
  useEffect(() => {
    if (!sttAvailable && conversationMode) {
      cancelRecording();
      setConversationMode(false);
    }
  }, [sttAvailable, conversationMode, cancelRecording]);

  // Clear press-to-submit overlay if recording ended without producing a transcript
  // (e.g. connection error, mic permission denied). In the normal flow,
  // handleTranscriptReady sets pressToSubmitActive=false in the same batch as
  // cleanup, so this effect will not fire spuriously.
  useEffect(() => {
    if (pressToSubmitActive && !isConnecting && !isRecording) {
      setPressToSubmitActive(false);
    }
  }, [pressToSubmitActive, isConnecting, isRecording]);

  // --- Handlers ---

  const handleMicClick = useCallback(() => {
    if (isPressToSubmit) {
      setPressToSubmitActive(true);
      setInputValueRef.current("");
      startRecording();
    } else if (conversationMode) {
      if (isRecording) {
        cancelRecording();
      }

      setConversationMode(false);
    } else {
      setConversationMode(true);
    }
  }, [
    isPressToSubmit,
    conversationMode,
    isRecording,
    startRecording,
    cancelRecording,
  ]);

  const handleCancelRecording = useCallback(() => {
    if (isPressToSubmit) {
      cancelRecording();
      setPressToSubmitActive(false);
    } else {
      cancelRecording();
      setConversationMode(false);
    }
  }, [isPressToSubmit, cancelRecording]);

  const handleSttSubmit = useCallback(() => {
    const text = inputValueRef.current;
    if (text.trim()) {
      cancelRecording();
      sendMessageRef.current(text);
      setInputValueRef.current("");
      setPressToSubmitActive(false);
    }
  }, [cancelRecording]);

  // Stop any active press-to-submit recording (without clearing input text).
  // Used when the user submits via Enter key while recording is still active.
  const stopPressToSubmitRecording = useCallback(() => {
    if (pressToSubmitActive) {
      cancelRecording();
      setPressToSubmitActive(false);
    }
  }, [pressToSubmitActive, cancelRecording]);

  const isWaiting = conversationMode && !isConnecting && !isRecording;

  const showOverlay =
    pressToSubmitActive || isConnecting || isRecording || conversationMode;

  const micTooltip = isPressToSubmit
    ? t("start_voice_input")
    : conversationMode
      ? t("switch_to_text_mode")
      : t("switch_to_conversation_mode");

  return {
    showOverlay,
    sttAvailable,
    conversationMode,
    handleMicClick,
    micTooltip,
    stopPressToSubmitRecording,
    overlayProps: {
      isPressToSubmit,
      isConnecting,
      isRecording,
      isWaiting,
      analyserNode,
      hasText: !!inputValue.trim(),
      onCancel: handleCancelRecording,
      onSubmit: handleSttSubmit,
    } satisfies SpeechToTextOverlayProps,
  };
};
